// One Bluetooth Mesh network (= one location) as seen from this phone:
// connect to a proxy, pair, configure, control. Ported from the sampler's
// MeshController (cob-mesh-reactjs/src/mesh/controller.ts) with three changes
// that the sampler, being one phone and one lamp, never needed:
//
//  1. THE KEYS COME FROM THE SERVER. NetKey/AppKey/IV index are the location's
//     (Location.Mesh* columns, GetMeshNetwork); a lamp's device key is on its
//     BleNode row. Nothing here generates or stores a network.
//
//  2. THIS PHONE HAS ITS OWN SOURCE ADDRESS AND COUNTER (meshClientStore).
//     The sampler used 0x0001 for everyone — fine for one phone, silent
//     command loss for two.
//
//  3. GROUPS ARE MESH GROUP ADDRESSES. Each parent and group node owns one
//     (0xC000+, BleNode.MeshAddress); every lamp SUBSCRIBES its lighting models
//     to its group's address and every ancestor's. A command to a group of 25
//     lamps is ONE message, and the mesh relays it — not 25 packets in turn.
//
// ⚠️ ONE PROXY CONNECTION AT A TIME, for the whole app. The proxy is any lamp of
// this network in range; it relays to the rest. Which one we reach is chosen
// by the Network ID it advertises (k3 of the NetKey) — NOT "the first 0x1828
// we see", which in a building with two networks would be the neighbour's lamp,
// and every command would vanish into it without an error.

import {
  MeshGatt, MSG, UUID, parseMeshAdvert, scanMesh,
} from './meshGatt';
import { MeshNetwork, encodeOpcode } from './meshNetwork';
import { provision } from './meshProvisioning';
import {
  aesEcb, k1, k3, s1,
} from './meshCrypto';
import {
  ascii, concat, equal, fromHex, hex, readU16le, u16be, u16le,
} from './bytes';
import { openClient } from './meshClientStore';
import { stopScan } from '../bleClient';

/* eslint-disable no-bitwise */

export const MODEL = {
  ONOFF: 0x1000, LEVEL: 0x1002, LIGHTNESS: 0x1300, CTL: 0x1303, CTL_TEMP: 0x1306,
};
/** Models the AppKey is bound to — the white-light set (no HSL: these lamps have none). */
const BIND_MODELS = [MODEL.ONOFF, MODEL.LEVEL, MODEL.LIGHTNESS, MODEL.CTL, MODEL.CTL_TEMP];
/** Models subscribed to group addresses — what a group command must reach. */
const SUB_MODELS = [MODEL.ONOFF, MODEL.LIGHTNESS, MODEL.CTL, MODEL.CTL_TEMP];

export const OP = {
  APPKEY_ADD: 0x00,
  APPKEY_STATUS: 0x8003,
  COMP_GET: 0x8008,
  COMP_STATUS: 0x02,
  BIND: 0x803d,
  BIND_STATUS: 0x803e,
  SUB_ADD: 0x801b,
  SUB_OVERWRITE: 0x801e,
  SUB_STATUS: 0x801f,
  RESET: 0x8049,
  RESET_STATUS: 0x804a,
  ONOFF_GET: 0x8201,
  ONOFF_SET: 0x8202,
  ONOFF_SET_UNACK: 0x8203,
  ONOFF_STATUS: 0x8204,
  LIGHTNESS_SET_UNACK: 0x824d,
  CTL_TEMP_SET_UNACK: 0x8265,
  CTL_GET: 0x825d,
  CTL_SET: 0x825e,
  CTL_SET_UNACK: 0x825f,
  CTL_STATUS: 0x8260,
  CTL_RANGE_GET: 0x8262,
  CTL_RANGE_STATUS: 0x8263,
};

const STATUS_TEXT = {
  0: 'Success',
  1: 'Invalid address',
  2: 'Invalid model',
  3: 'Invalid AppKey index',
  4: 'Invalid NetKey index',
  5: 'Insufficient resources',
  6: 'Key index already stored',
  8: 'Not a subscribe model',
  0xb: 'Cannot bind',
};

export const isGroupAddress = (a) => Number(a) >= 0xc000 && Number(a) <= 0xfeff;

const sleep = (ms) => new Promise((r) => { setTimeout(r, ms); });
const addr = (a) => `0x${Number(a).toString(16).padStart(4, '0')}`;
/*
 * ⚠️ HOLD THE LAMP AS BRIEFLY AS IS USEFUL. A Telink lamp takes ONE GATT
 * connection and stops advertising while it has it — so while this phone holds
 * it, a second phone near only that lamp finds NOTHING ("No lamp of this network
 * found nearby", seen on hardware 2026-10-10 with 60 s here). Short enough that
 * another phone gets in, long enough that slider drags do not reconnect each time.
 * The link is also dropped the moment the app goes to the background (below).
 */
const IDLE_DISCONNECT_MS = 15000;
const RELEASE_MS = 1500;
const SCAN_FOR_PROXY_MS = 10000;

/*
 * ⚠️ RECOGNISE A LAMP BY WHAT IT ADVERTISES, NOT BY THE PHONE THAT PAIRED IT.
 * MeshMac is the BLE id the PAIRING phone saw. On Android that is the lamp's MAC
 * and every phone sees the same one; on iOS it is a per-phone UUID that no other
 * phone will ever see. A lamp advertising Node Identity instead of the Network
 * ID could then only be found by the phone that paired it — which looks exactly
 * like "it only works on the phone that paired it" and tempts re-pairing per
 * phone. So Node Identity is checked against our lamps' ADDRESSES (Mesh Profile
 * 7.2.2.2.4): Hash = e(IdentityKey, 0x00 x6 || Random || Address)[8..15].
 */
export function identityKeyOf(netKey) {
  return k1(netKey, s1(ascii('nkik')), concat(ascii('id128'), Uint8Array.of(0x01)));
}
export function matchesNodeIdentity(identityKey, nodeIdentity, address) {
  const block = concat(new Uint8Array(6), nodeIdentity.random, u16be(address));
  return equal(aesEcb(identityKey, block).slice(8, 16), nodeIdentity.hash);
}

/** Composition Data page 0 -> { cid, pid, crpl, elements: [{ index, address, sig: [ids] }] } */
export function parseComposition(p, primary) {
  const cid = readU16le(p, 1);
  const pid = readU16le(p, 3);
  const crpl = readU16le(p, 7);
  const elements = [];
  let o = 11;
  let idx = 0;
  while (o + 4 <= p.length) {
    const numS = p[o + 2];
    const numV = p[o + 3];
    o += 4;
    const sig = [];
    for (let i = 0; i < numS && o + 2 <= p.length; i += 1, o += 2) sig.push(readU16le(p, o));
    o += numV * 4; // vendor models: skipped, we never address them
    elements.push({ index: idx, address: primary + idx, sig });
    idx += 1;
  }
  return {
    cid, pid, crpl, elements,
  };
}

/** The element that holds `model`, from a lamp row's stored composition. */
export function elementWith(lamp, model) {
  const els = parseElements(lamp?.MeshComposition);
  return els.find((e) => (e.sig || []).includes(model))?.address;
}

export function parseElements(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try {
    const v = JSON.parse(raw);
    return Array.isArray(v) ? v : (Array.isArray(v?.elements) ? v.elements : []);
  } catch (e) {
    return [];
  }
}

/** Why no proxy was found — said precisely, because each case has a different fix. */
export function noProxyMessage(seen, lastErr) {
  if (!seen || seen.total === 0) {
    return 'No lamp is advertising nearby. If another phone is using these lamps, the lamp it is '
      + 'connected to stays silent until it lets go (about 15 s) — try again then. Otherwise move '
      + 'closer and check the lamps are powered.'
      + (lastErr ? ` (${lastErr.message})` : '');
  }
  if (seen.otherNetworks === seen.total) {
    return `Found ${seen.total} mesh lamp(s), but none on this location's network — they belong to `
      + 'another network (another location, or paired by a different app). Factory-reset them and add them here.';
  }
  return `Found ${seen.total} mesh lamp(s) but could not recognise any of them as this group's. `
    + 'Try again in a few seconds.';
}

// ── the session ───────────────────────────────────────────────────────────────

let current = null; // the one live session (one proxy connection app-wide)

// The app going to the background must not keep a lamp's only connection —
// another phone could not reach that lamp until this one came back.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden' && current) {
      clearTimeout(current.idleTimer);
      current.gatt.disconnect();
    }
  });
}

/**
 * network: { LocationID, NetKey, AppKey, IvIndex, ClientAddress } from GetMeshNetwork.
 * Reuses the live session if it is for the same network and phone address.
 */
export function openSession(network, log = () => {}) {
  const key = `${network.LocationID}:${network.NetKey}:${network.ClientAddress}`;
  if (current && current.key === key) {
    current.log = log;
    return current;
  }
  if (current) current.close();
  current = new MeshSession(network, key, log);
  return current;
}

export class MeshSession {
  constructor(network, key, log) {
    this.key = key;
    this.log = log;
    this.locationId = network.LocationID;
    this.netKey = fromHex(network.NetKey);
    this.networkId = hex(k3(this.netKey));
    this.identityKey = identityKeyOf(this.netKey);
    this.client = openClient(network.LocationID, this.networkId, Number(network.ClientAddress));
    this.devKeys = new Map(); // primary unicast -> { key: Uint8Array, count }
    this.net = new MeshNetwork({
      netKey: this.netKey,
      appKey: fromHex(network.AppKey),
      ivIndex: Number(network.IvIndex) || 0,
      src: this.client.address,
      nextSeq: this.client.nextSeq,
      devKeyFor: (a) => this.devKeyFor(a),
    });
    this.gatt = new MeshGatt((s) => this.log(s));
    this.gatt.onNetworkPdu = (pdu) => this.onNetworkPdu(pdu);
    this.gatt.onDisconnect = () => { this.proxyId = null; };
    this.proxyId = null;
    this.waiters = [];
    this.tid = Math.floor(Math.random() * 255);
    this.idleTimer = null;
    this.connecting = null;
  }

  /** Lamp rows carrying MeshDevKey (owner / pairer only) — needed for configure, reset. */
  setDevKeys(lamps) {
    for (const l of lamps || []) {
      if (l?.MeshDevKey && l?.MeshAddress) {
        this.devKeys.set(Number(l.MeshAddress), { key: fromHex(l.MeshDevKey), count: Math.max(1, Number(l.MeshElements) || 1) });
      }
    }
  }

  devKeyFor(a) {
    for (const [base, v] of this.devKeys) if (a >= base && a < base + v.count) return v.key;
    return undefined;
  }

  nextTid() {
    this.tid = (this.tid + 1) & 0xff;
    return this.tid;
  }

  // ── proxy connection ───────────────────────────────────────────────────────

  touch() {
    clearTimeout(this.idleTimer);
    this.idleTimer = setTimeout(() => { this.gatt.disconnect(); }, IDLE_DISCONNECT_MS);
  }

  /**
   * Make sure we are connected to a proxy of THIS network.
   * preferIds: BLE device ids worth trying first (a lamp just paired, or the
   * lamps' stored MeshMac). Otherwise scan up to 8 s for a proxy advertising our
   * Network ID — or one whose id we already know.
   */
  async ensureProxy({
    preferIds = [], knownIds = [], knownAddresses = [], scanMs = SCAN_FOR_PROXY_MS,
  } = {}) {
    if (this.gatt.connected && this.gatt.kind === 'proxy') { this.touch(); return; }
    if (this.connecting) { await this.connecting; return; }
    this.connecting = (async () => {
      let lastErr = null;
      for (const id of preferIds.filter(Boolean)) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await this.gatt.connect(id, 'proxy');
          this.proxyId = id;
          break;
        } catch (e) {
          lastErr = e;
        }
      }
      if (!this.gatt.connected) {
        this.log('Looking for a lamp of this network…');
        const known = new Set(knownIds.filter(Boolean));
        const { id, seen } = await this.findProxy(known, knownAddresses, scanMs);
        if (!id) throw new Error(noProxyMessage(seen, lastErr));
        await this.gatt.connect(id, 'proxy');
        this.proxyId = id;
      }
      // Ask the proxy to forward everything to us: reject-list filter, empty.
      await this.gatt.send(MSG.PROXY_CONFIG, this.net.encodeProxyConfig(0x00, Uint8Array.of(0x01)));
      this.touch();
    })();
    try {
      await this.connecting;
    } finally {
      this.connecting = null;
    }
  }

  /**
   * Scan for a proxy of THIS network: by Network ID, by a BLE id we know, or by
   * a Node Identity that hashes to one of our lamps' addresses.
   * -> { id | null, seen: { total, otherNetworks, unknownIdentity } }
   */
  async findProxy(knownIds, knownAddresses, ms) {
    const seen = { total: new Set(), otherNetworks: new Set(), unknownIdentity: new Set() };
    const addresses = (knownAddresses || []).map(Number).filter((a) => a > 0 && a < 0x8000);
    let resolveFound;
    const found = new Promise((r) => { resolveFound = r; });
    const stop = await scanMesh((d) => {
      if (d.kind !== 'proxy') return;
      seen.total.add(d.deviceId);
      if (d.networkId === this.networkId || knownIds.has(d.deviceId)) { resolveFound(d.deviceId); return; }
      if (d.nodeIdentity) {
        if (addresses.some((a) => matchesNodeIdentity(this.identityKey, d.nodeIdentity, a))) { resolveFound(d.deviceId); return; }
        seen.unknownIdentity.add(d.deviceId);
      } else if (d.networkId) {
        seen.otherNetworks.add(d.deviceId);
      }
    }, { timeoutMs: ms, services: [UUID.PROXY_SERVICE] });
    const id = await Promise.race([found, sleep(ms).then(() => null)]);
    await stop();
    return {
      id,
      seen: {
        total: seen.total.size, otherNetworks: seen.otherNetworks.size, unknownIdentity: seen.unknownIdentity.size,
      },
    };
  }

  close() {
    clearTimeout(this.idleTimer);
    this.gatt.disconnect();
    if (current === this) current = null;
  }

  /** The screen went away: drop the link soon, unless another screen picks it up. */
  release() {
    clearTimeout(this.idleTimer);
    this.idleTimer = setTimeout(() => { this.gatt.disconnect(); }, RELEASE_MS);
  }

  // ── messaging ──────────────────────────────────────────────────────────────

  async onNetworkPdu(pdu) {
    const d = this.net.decodeNetworkPdu(pdu);
    if (!d) return;
    const r = this.net.handleIncoming(d);
    for (const reply of r.reply) {
      // eslint-disable-next-line no-await-in-loop
      await this.gatt.send(MSG.NETWORK, reply).catch(() => {});
    }
    if (!r.access) return;
    const m = r.access;
    const i = this.waiters.findIndex((w) => w.match(m));
    if (i >= 0) this.waiters.splice(i, 1)[0].resolve(m);
  }

  waitFor(match, ms) {
    return new Promise((resolve) => {
      const w = { match, resolve: (m) => { clearTimeout(t); resolve(m); } };
      const t = setTimeout(() => { this.waiters = this.waiters.filter((x) => x !== w); resolve(undefined); }, ms);
      this.waiters.push(w);
    });
  }

  async sendAccess(dst, opcode, params, devKey) {
    if (!this.gatt.connected) throw new Error('Not connected to the lamps');
    for (const p of this.net.encodeAccess(dst, concat(encodeOpcode(opcode), params), devKey)) {
      // eslint-disable-next-line no-await-in-loop
      await this.gatt.send(MSG.NETWORK, p);
    }
    this.touch();
  }

  /** Send and wait for a status from `dst`, retrying. */
  async request(dst, opcode, params, devKey, statusOp, ms = 5000, tries = 3) {
    for (let i = 0; i < tries; i += 1) {
      const wait = this.waitFor((m) => m.opcode === statusOp && m.src === dst, ms);
      // eslint-disable-next-line no-await-in-loop
      await this.sendAccess(dst, opcode, params, devKey);
      // eslint-disable-next-line no-await-in-loop
      const m = await wait;
      if (m) return m;
    }
    throw new Error(`No reply from ${addr(dst)} to 0x${opcode.toString(16)}`);
  }

  // ── pairing ────────────────────────────────────────────────────────────────

  /**
   * Pair one unprovisioned lamp. `allocate(numElements)` -> Promise<address>
   * (the server's AllocateMeshAddress). -> { devKey (hex), address, numElements }
   * The lamp reboots as a proxy afterwards; configure() comes next.
   */
  async pair(device, allocate, { staticOOB } = {}) {
    await stopScan();
    await this.gatt.disconnect();
    try {
      await this.gatt.connect(device.deviceId, 'prov');
      const res = await provision(
        {
          send: (p) => this.gatt.send(MSG.PROVISIONING, p),
          next: (t) => this.gatt.nextProv(t),
          log: (s) => this.log(s),
        },
        {
          netKey: this.netKey, netKeyIndex: 0, ivIndex: this.net.keys.ivIndex, unicast: allocate, staticOOB,
        },
      );
      this.devKeys.set(res.unicast, { key: res.devKey, count: Math.max(1, res.numElements) });
      return { devKey: hex(res.devKey), address: res.unicast, numElements: res.numElements };
    } finally {
      await this.gatt.disconnect();
    }
  }

  /**
   * Configure a paired lamp: read its composition, give it the AppKey, bind the
   * lighting models, subscribe them to `groupAddresses`, read its temperature
   * range. -> { composition: [{index,address,sig}], cid, pid, crpl, tempRange? }
   */
  /**
   * subs = { groups, swapGroups, swapped } — see subscribe().
   */
  async configure(lamp, subs, { preferIds = [], knownIds = [] } = {}) {
    const address = Number(lamp.MeshAddress);
    if (!this.devKeyFor(address)) throw new Error('Only the person who paired this lamp (or the owner) can configure it.');
    // Right after pairing the lamp advertises Node Identity, not our Network
    // ID, so it is recognised by its BLE id (preferIds) instead.
    await this.ensureProxy({ preferIds, knownIds: [...preferIds, ...knownIds] });
    this.log(`Configuring ${lamp.Name || addr(address)}…`);
    const comp = await this.request(address, OP.COMP_GET, Uint8Array.of(0), true, OP.COMP_STATUS, 6000);
    const parsed = parseComposition(comp.params, address);
    this.log(`Composition: ${parsed.elements.length} element(s), replay list ${parsed.crpl}`);

    const ak = await this.request(address, OP.APPKEY_ADD, concat(Uint8Array.of(0, 0, 0), this.net.keys.appKey), true, OP.APPKEY_STATUS);
    if (ak.params[0] !== 0 && ak.params[0] !== 6) throw new Error(`The lamp refused the app key: ${STATUS_TEXT[ak.params[0]] ?? ak.params[0]}`);

    // A failed bind must not skip the rest: CTL Temperature sits on a later element.
    for (const e of parsed.elements) {
      for (const m of e.sig.filter((x) => BIND_MODELS.includes(x))) {
        // eslint-disable-next-line no-await-in-loop
        const st = await this.request(address, OP.BIND, concat(u16le(e.address), u16le(0), u16le(m)), true, OP.BIND_STATUS)
          .catch((err) => { this.log(`Bind 0x${m.toString(16)} failed: ${err.message}`); return undefined; });
        if (st && st.params[0] !== 0) this.log(`Bind 0x${m.toString(16)}: ${STATUS_TEXT[st.params[0]] ?? st.params[0]}`);
      }
    }

    const withComp = { ...lamp, MeshComposition: JSON.stringify(parsed.elements) };
    await this.subscribe(withComp, subs);
    const tempRange = await this.readTempRange(withComp);
    this.log('Configured');
    return {
      composition: parsed.elements, cid: parsed.cid, pid: parsed.pid, crpl: parsed.crpl, tempRange,
    };
  }

  /**
   * Make the lamp's subscriptions EXACTLY its group + every ancestor's:
   * Overwrite with the first, Add the rest. Run after configure, after a group
   * moves to another parent, and when the reversed-wiring flag changes.
   *
   * subs = { groups, swapGroups, swapped }. On/off and level listen on
   * `groups`. COLOUR (the CTL Temperature server) listens on `groups` — or, on
   * a lamp with REVERSED LEDs, on `swapGroups`, where group commands send the
   * mirrored colour. (A plain array is still accepted: the old call shape.)
   */
  async subscribe(lamp, subs) {
    const address = Number(lamp.MeshAddress);
    const norm = (list) => [...new Set((list || []).map(Number).filter(isGroupAddress))];
    const { groups: rawGroups, swapGroups: rawSwap, swapped } = Array.isArray(subs) ? { groups: subs } : (subs || {});
    const groups = norm(rawGroups);
    const swapGroups = norm(rawSwap);
    if (groups.length === 0) return;
    const els = parseElements(lamp.MeshComposition);
    let failures = 0;
    for (const e of els) {
      for (const m of (e.sig || []).filter((x) => SUB_MODELS.includes(x))) {
        const target = m === MODEL.CTL_TEMP && swapped && swapGroups.length ? swapGroups : groups;
        for (let i = 0; i < target.length; i += 1) {
          const op = i === 0 ? OP.SUB_OVERWRITE : OP.SUB_ADD;
          // eslint-disable-next-line no-await-in-loop
          const st = await this.request(address, op, concat(u16le(e.address), u16le(target[i]), u16le(m)), true, OP.SUB_STATUS, 4000, 2)
            .catch(() => undefined);
          if (!st || st.params[0] !== 0) {
            failures += 1;
            this.log(`Subscribe 0x${m.toString(16)} → ${addr(target[i])}: ${st ? (STATUS_TEXT[st.params[0]] ?? st.params[0]) : 'no reply'}`);
          }
        }
      }
    }
    if (failures && !els.length) throw new Error('No composition data for this lamp — configure it again.');
  }

  async readTempRange(lamp) {
    const dst = elementWith(lamp, MODEL.CTL) ?? Number(lamp.MeshAddress);
    const st = await this.request(dst, OP.CTL_RANGE_GET, new Uint8Array(0), false, OP.CTL_RANGE_STATUS, 2500, 2).catch(() => undefined);
    if (!st || st.params.length < 5) return undefined;
    return { min: readU16le(st.params, 1), max: readU16le(st.params, 3) };
  }

  /** Factory-reset the lamp (it forgets this network). Needs the device key. */
  async reset(lamp, opts = {}) {
    const address = Number(lamp.MeshAddress);
    if (!this.devKeyFor(address)) throw new Error('Only the person who paired this lamp (or the owner) can reset it.');
    await this.ensureProxy(opts);
    await this.request(address, OP.RESET, new Uint8Array(0), true, OP.RESET_STATUS, 4000, 2);
    this.devKeys.delete(address);
  }

  // ── control ────────────────────────────────────────────────────────────────
  // dst is a lamp's primary unicast address or a node's group address. To a
  // group: unacknowledged and sent twice (same TID, so a lamp acts once) — 25
  // replies to one press would only flood the proxy. To one lamp: acknowledged,
  // which also tells us it is really there.

  /**
   * opts.ack = false sends a single unacknowledged set even to one lamp — for
   * blinking, where a steady rhythm matters more than a reply per step.
   */
  async onOff(dst, on, opts = {}) {
    await this.ensureProxy(opts);
    const tid = this.nextTid();
    // opts.instant: transition time 0 + delay 0 — no fade. A lamp's default
    // fade otherwise swallows a blink's dark moment.
    const params = opts.instant ? Uint8Array.of(on ? 1 : 0, tid, 0x00, 0x00) : Uint8Array.of(on ? 1 : 0, tid);
    if (opts.ack === false && !isGroupAddress(dst)) {
      // Twice, same TID (the lamp acts once): an unacknowledged step that is
      // lost would break the blink's rhythm.
      await this.sendAccess(dst, OP.ONOFF_SET_UNACK, params, false);
      await sleep(60);
      await this.sendAccess(dst, OP.ONOFF_SET_UNACK, params, false);
      return null;
    }
    if (isGroupAddress(dst)) {
      await this.sendAccess(dst, OP.ONOFF_SET_UNACK, params, false);
      await sleep(120);
      await this.sendAccess(dst, OP.ONOFF_SET_UNACK, params, false);
      return null;
    }
    const st = await this.request(dst, OP.ONOFF_SET, params, false, OP.ONOFF_STATUS, 2500, 2);
    return st.params[st.params.length >= 3 ? 1 : 0] === 1;
  }

  /**
   * What the lamp ITSELF says it is doing — not what a phone last sent. Works
   * only while connected (the lamp cannot push to an idle phone), so it is
   * read when a screen shows the lamps. A transition in progress reports its
   * TARGET. -> { on, lightness|null, kelvin|null }; throws if the lamp is silent.
   */
  async readState(lamp) {
    const base = Number(lamp.MeshAddress);
    const onDst = elementWith(lamp, MODEL.ONOFF) ?? base;
    const st = await this.request(onDst, OP.ONOFF_GET, new Uint8Array(0), false, OP.ONOFF_STATUS, 1500, 2);
    const on = (st.params.length >= 2 ? st.params[1] : st.params[0]) === 1;
    let lightness = null;
    let kelvin = null;
    const ctlDst = elementWith(lamp, MODEL.CTL);
    if (ctlDst != null) {
      const c = await this.request(ctlDst, OP.CTL_GET, new Uint8Array(0), false, OP.CTL_STATUS, 1500, 1).catch(() => null);
      if (c && c.params.length >= 4) {
        const o = c.params.length >= 8 ? 4 : 0; // target fields when transitioning
        lightness = readU16le(c.params, o);
        kelvin = readU16le(c.params, o + 2);
      }
    }
    return { on, lightness, kelvin };
  }

  /** Group only: Light Lightness Set (unacknowledged, sent twice, same TID). */
  async lightness(dst, lightness, opts = {}) {
    await this.ensureProxy(opts);
    const params = concat(u16le(lightness), Uint8Array.of(this.nextTid()));
    await this.sendAccess(dst, OP.LIGHTNESS_SET_UNACK, params, false);
    await sleep(120);
    await this.sendAccess(dst, OP.LIGHTNESS_SET_UNACK, params, false);
  }

  /** Group only: Light CTL Temperature Set (unacknowledged, sent twice, same TID). */
  async temperature(dst, kelvin, opts = {}) {
    await this.ensureProxy(opts);
    const params = concat(u16le(kelvin), u16le(0), Uint8Array.of(this.nextTid()));
    await this.sendAccess(dst, OP.CTL_TEMP_SET_UNACK, params, false);
    await sleep(120);
    await this.sendAccess(dst, OP.CTL_TEMP_SET_UNACK, params, false);
  }

  /** Light CTL Set: lightness 0..65535 + kelvin, to the lamp's CTL Server (or a group). */
  async ctl(dst, lightness, kelvin, opts = {}) {
    await this.ensureProxy(opts);
    const tid = this.nextTid();
    const params = concat(u16le(lightness), u16le(kelvin), u16le(0), Uint8Array.of(tid));
    if (isGroupAddress(dst)) {
      await this.sendAccess(dst, OP.CTL_SET_UNACK, params, false);
      await sleep(120);
      await this.sendAccess(dst, OP.CTL_SET_UNACK, params, false);
      return null;
    }
    const st = await this.request(dst, OP.CTL_SET, params, false, OP.CTL_STATUS, 2500, 2);
    return { lightness: readU16le(st.params, 0), kelvin: readU16le(st.params, 2) };
  }
}

/** For the tests: the advert parser, re-exported so tests need not import the radio module. */
export { parseMeshAdvert };
